import { supabase } from '../lib/supabase';

export const automatedPaymentService = {
  /**
   * Get all automated payments for a company
   */
  async getAutomatedPayments(companyId) {
    try {
      const { data, error } = await supabase
        ?.from('automated_payment_queue')
        ?.select(`
          *,
          user:user_id (
            id,
            email,
            full_name
          )
        `)
        ?.eq('company_id', companyId)
        ?.order('scheduled_charge_date', { ascending: false });

      if (error) throw error;

      return data?.map(payment => ({
        id: payment?.id,
        userId: payment?.user_id,
        userEmail: payment?.user?.email,
        userFullName: payment?.user?.full_name,
        trialEndDate: payment?.trial_end_date,
        scheduledChargeDate: payment?.scheduled_charge_date,
        amount: payment?.amount,
        currency: payment?.currency,
        status: payment?.status,
        retryCount: payment?.retry_count,
        maxRetries: payment?.max_retries,
        lastRetryAt: payment?.last_retry_at,
        nextRetryAt: payment?.next_retry_at,
        stripePaymentIntentId: payment?.stripe_payment_intent_id,
        errorMessage: payment?.error_message,
        processedAt: payment?.processed_at,
        createdAt: payment?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching automated payments:', error);
      throw error;
    }
  },

  /**
   * Get payment retry logs
   */
  async getPaymentRetryLogs(automatedPaymentId) {
    try {
      const { data, error } = await supabase
        ?.from('payment_retry_log')
        ?.select('*')
        ?.eq('automated_payment_id', automatedPaymentId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        retryAttempt: log?.retry_attempt,
        status: log?.status,
        stripePaymentIntentId: log?.stripe_payment_intent_id,
        errorCode: log?.error_code,
        errorMessage: log?.error_message,
        retryDelaySeconds: log?.retry_delay_seconds,
        nextRetryScheduledAt: log?.next_retry_scheduled_at,
        createdAt: log?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching retry logs:', error);
      throw error;
    }
  },

  /**
   * Get billing notifications
   */
  async getBillingNotifications(companyId, filters = {}) {
    try {
      let query = supabase
        ?.from('billing_notifications')
        ?.select('*')
        ?.eq('company_id', companyId);

      if (filters?.notificationType) {
        query = query?.eq('notification_type', filters?.notificationType);
      }

      if (filters?.isSent !== undefined) {
        query = query?.eq('is_sent', filters?.isSent);
      }

      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(notification => ({
        id: notification?.id,
        notificationType: notification?.notification_type,
        subject: notification?.subject,
        message: notification?.message,
        recipientEmail: notification?.recipient_email,
        isSent: notification?.is_sent,
        sentAt: notification?.sent_at,
        emailMessageId: notification?.email_message_id,
        errorMessage: notification?.error_message,
        createdAt: notification?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching billing notifications:', error);
      throw error;
    }
  },

  /**
   * Trigger automated payment processing
   */
  async processAutomatedPayments() {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();

      const response = await fetch(
        `${supabase?.supabaseUrl}/functions/v1/process-automated-payments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          }
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Failed to process automated payments');
      }

      const result = await response?.json();
      return result;
    } catch (error) {
      console.error('Error processing automated payments:', error);
      throw error;
    }
  },

  /**
   * Send billing notification
   */
  async sendBillingNotification(notificationId) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();

      const response = await fetch(
        `${supabase?.supabaseUrl}/functions/v1/send-billing-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({ notificationId })
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Failed to send billing notification');
      }

      const result = await response?.json();
      return result;
    } catch (error) {
      console.error('Error sending billing notification:', error);
      throw error;
    }
  },

  /**
   * Get payment statistics
   */
  async getPaymentStatistics(companyId) {
    try {
      const payments = await this.getAutomatedPayments(companyId);

      const totalPayments = payments?.length || 0;
      const successfulPayments = payments?.filter(p => p?.status === 'succeeded')?.length || 0;
      const failedPayments = payments?.filter(p => p?.status === 'failed')?.length || 0;
      const exhaustedPayments = payments?.filter(p => p?.status === 'exhausted')?.length || 0;
      const pendingPayments = payments?.filter(p => p?.status === 'pending')?.length || 0;
      const processingPayments = payments?.filter(p => p?.status === 'processing')?.length || 0;

      const totalAmount = payments?.reduce((sum, p) => sum + (p?.amount || 0), 0);
      const successfulAmount = payments
        ?.filter(p => p?.status === 'succeeded')
        ?.reduce((sum, p) => sum + (p?.amount || 0), 0);

      const successRate = totalPayments > 0 
        ? ((successfulPayments / totalPayments) * 100)?.toFixed(1)
        : 0;

      return {
        totalPayments,
        successfulPayments,
        failedPayments,
        exhaustedPayments,
        pendingPayments,
        processingPayments,
        totalAmount,
        successfulAmount,
        successRate
      };
    } catch (error) {
      console.error('Error calculating payment statistics:', error);
      throw error;
    }
  },

  /**
   * Format payment status for display
   */
  getStatusDisplay(status) {
    const statusMap = {
      'pending': { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
      'processing': { label: 'Processing', color: 'bg-blue-100 text-blue-800', icon: '⚙️' },
      'succeeded': { label: 'Succeeded', color: 'bg-green-100 text-green-800', icon: '✅' },
      'failed': { label: 'Failed', color: 'bg-red-100 text-red-800', icon: '❌' },
      'exhausted': { label: 'Exhausted', color: 'bg-gray-100 text-gray-800', icon: '🚫' }
    };

    return statusMap?.[status] || { label: status, color: 'bg-gray-100 text-gray-800', icon: '❓' };
  },

  /**
   * Format notification type for display
   */
  getNotificationTypeDisplay(type) {
    const typeMap = {
      'payment_success': { label: 'Payment Success', color: 'bg-green-100 text-green-800', icon: '✅' },
      'payment_failed': { label: 'Payment Failed', color: 'bg-red-100 text-red-800', icon: '❌' },
      'payment_retry': { label: 'Payment Retry', color: 'bg-yellow-100 text-yellow-800', icon: '🔄' },
      'trial_expiring': { label: 'Trial Expiring', color: 'bg-orange-100 text-orange-800', icon: '⏰' },
      'trial_expired': { label: 'Trial Expired', color: 'bg-red-100 text-red-800', icon: '⏱️' },
      'payment_method_required': { label: 'Payment Method Required', color: 'bg-yellow-100 text-yellow-800', icon: '💳' }
    };

    return typeMap?.[type] || { label: type, color: 'bg-gray-100 text-gray-800', icon: '📧' };
  },

  /**
   * Format amount
   */
  formatAmount(amount, currency = 'PGK') {
    return new Intl.NumberFormat('en-PG', {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'code'
    })?.format(amount)?.replace('PGK', 'K');
  },

  /**
   * Calculate days until scheduled charge
   */
  calculateDaysUntilCharge(scheduledDate) {
    if (!scheduledDate) return null;
    const scheduled = new Date(scheduledDate);
    const today = new Date();
    const diffTime = scheduled - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }
};

export default automatedPaymentService;
